import React from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, Check, ArrowRight, Sparkles, ChevronRight,
  ExternalLink, ShieldAlert, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import SEO, { kitProduct } from '../../components/SEO';

// Shared landing layout used by all 4 kit pages (Garzón, Auto, Inmo, Legal).
// Every kit passes a `config` prop describing hero, features, flow steps,
// mockup panels, use-cases, and optional special sections (leadTypes for
// Inmo, practiceAreas for Legal, disclaimer + onboarding banner for Legal).
export const KitLandingLayout = ({ config }) => {
  const accent = config.accent || 'indigo';
  const accentHex = config.accentHex || '#4F46E5';

  const seoPath = `/kits/${config.slug}`;
  const jsonLd = kitProduct({
    name: config.name,
    description: config.seoDescription || config.tagline,
    path: seoPath,
  });

  // Tailwind tokens per accent. Kept explicit so purge doesn't strip them.
  const T = {
    indigo: {
      textAcc: 'text-indigo-600', bgAcc: 'bg-indigo-600', bgAccHover: 'hover:bg-indigo-700',
      bgSoft: 'bg-indigo-50', borderAcc: 'border-indigo-200', ring: 'ring-indigo-100',
      pillBg: 'bg-indigo-100', pillText: 'text-indigo-700',
    },
    orange: {
      textAcc: 'text-orange-600', bgAcc: 'bg-orange-500', bgAccHover: 'hover:bg-orange-600',
      bgSoft: 'bg-orange-50', borderAcc: 'border-orange-200', ring: 'ring-orange-100',
      pillBg: 'bg-orange-100', pillText: 'text-orange-700',
    },
  }[accent];

  return (
    <div className="min-h-screen bg-white" data-testid={`kit-page-${config.slug}`}>
      <SEO
        title={config.seoTitle}
        description={config.seoDescription || config.tagline}
        path={seoPath}
        image={`https://acbothub.com/og-${config.slug}.png`}
        jsonLd={jsonLd}
      />
      {/* Navbar (same look & feel as landing) */}
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="kit-nav-home">
            <MessageSquare className="w-7 h-7 text-indigo-600" />
            <span className="text-xl font-bold tracking-tight text-gray-950">ACBotHub</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-gray-700 hover:text-gray-950">Iniciar sesión</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg transition-all rounded-lg font-medium px-6 py-2.5">
                Comenzar
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 px-6 max-w-7xl mx-auto">
        <nav className="text-sm text-gray-500 flex items-center gap-2">
          <Link to="/" className="hover:text-gray-800">Inicio</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-800 font-medium">{config.breadcrumb}</span>
        </nav>
      </div>

      {/* HERO */}
      <section className="px-6 pb-16" data-testid={`${config.slug}-hero`}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-[auto,1fr] gap-8 items-center">
          <div className={`w-24 h-24 md:w-32 md:h-32 rounded-3xl ${T.bgSoft} border ${T.borderAcc} flex items-center justify-center shadow-sm`}>
            {config.heroLogo ? (
              <img src={config.heroLogo} alt={`${config.name} logo`} className="w-20 h-20 md:w-28 md:h-28 object-contain" />
            ) : (
              <span className="text-5xl md:text-6xl" aria-hidden>{config.heroEmoji}</span>
            )}
          </div>
          <div>
            <div className={`inline-flex items-center gap-2 ${T.pillBg} ${T.pillText} px-3 py-1 rounded-full text-xs font-semibold mb-4`}>
              <Sparkles className="w-3.5 h-3.5" /> Kit de industria
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-950 mb-4">
              {config.title}
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl">{config.tagline}</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register">
                <Button
                  data-testid={`${config.slug}-cta-primary`}
                  size="lg"
                  className={`${T.bgAcc} ${T.bgAccHover} text-white shadow-lg hover:shadow-xl transition-all rounded-xl font-semibold px-8 py-6 text-base`}
                >
                  Empezar prueba gratis
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  data-testid={`${config.slug}-cta-secondary`}
                  variant="outline"
                  size="lg"
                  className="rounded-xl font-semibold px-8 py-6 text-base border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Ver planes
                </Button>
              </Link>
            </div>

            {config.disclaimer && (
              <div className="mt-5 inline-flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-2xl">
                <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">{config.disclaimer}</p>
              </div>
            )}

            {/* Stats */}
            <div className="mt-8 flex flex-wrap gap-6 text-sm">
              {config.stats.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-700" data-testid={`${config.slug}-stat-${i}`}>
                  <span className={`w-1.5 h-1.5 rounded-full`} style={{ backgroundColor: accentHex }} />
                  <span><strong className="text-gray-950">{s.value}</strong> <span className="text-gray-500">{s.label}</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Slot for a kit-specific section rendered right after the hero (e.g.
          the phone chat demo on GarzónBot). Other kits leave this undefined. */}
      {config.afterHero}

      {/* Optional: lead types (Inmo) */}
      {config.leadTypes && (
        <section className="py-14 px-6 bg-gray-50 border-y border-gray-200" data-testid={`${config.slug}-lead-types`}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-950 text-center mb-2">Diseñado para ambos lados del negocio</h2>
            <p className="text-gray-600 text-center max-w-2xl mx-auto mb-10">
              {config.name} detecta en los primeros mensajes cuál es cada persona y sigue el camino correcto automáticamente.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {config.leadTypes.map((lt, i) => (
                <div key={i} className={`bg-white border-2 ${T.borderAcc} rounded-2xl p-6 shadow-sm`} data-testid={`${config.slug}-leadtype-${i}`}>
                  <div className={`inline-flex items-center gap-2 ${T.pillBg} ${T.pillText} px-2.5 py-1 rounded-full text-xs font-semibold mb-3`}>
                    {lt.badge}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-950 mb-2">{lt.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{lt.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURES */}
      <section className="py-16 px-6" data-testid={`${config.slug}-features`}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 text-center mb-3 flex items-center justify-center gap-3">
            {config.titleIcon && (
              <img
                src={config.titleIcon}
                alt={`${config.name} icon`}
                className="w-9 h-9 sm:w-11 sm:h-11 object-contain shrink-0"
                data-testid={`${config.slug}-features-title-icon`}
              />
            )}
            <span>Todo lo que hace {config.name}</span>
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Funciones diseñadas específicamente para {config.industry}.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {config.features.map((f, i) => (
              <div
                key={i}
                className={`bg-white border rounded-2xl p-6 transition-all ${f.highlight ? `${T.borderAcc} shadow-lg ring-1 ${T.ring}` : 'border-gray-200 shadow-sm hover:shadow-md'}`}
                data-testid={`${config.slug}-feature-${i}`}
              >
                {f.highlight && (
                  <div className={`inline-flex items-center gap-1.5 ${T.bgAcc} text-white text-[10px] font-bold px-2 py-0.5 rounded-full mb-3 uppercase tracking-wide`}>
                    <Sparkles className="w-3 h-3" /> Destacado
                  </div>
                )}
                <h3 className="text-lg font-semibold text-gray-950 mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLOW */}
      <section className="py-16 px-6 bg-gray-50" data-testid={`${config.slug}-flow`}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 text-center mb-3">
            Cómo funciona
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
            {config.flowSubtitle}
          </p>

          <ol className="space-y-5">
            {config.flow.map((step, i) => (
              <li
                key={i}
                className={`flex gap-5 items-start bg-white border rounded-2xl p-5 sm:p-6 transition-all ${step.highlight ? `${T.borderAcc} shadow-lg` : 'border-gray-200 shadow-sm'}`}
                data-testid={`${config.slug}-flow-step-${i + 1}`}
              >
                <div className={`shrink-0 w-11 h-11 rounded-xl ${step.highlight ? T.bgAcc : 'bg-gray-100'} ${step.highlight ? 'text-white' : 'text-gray-700'} flex items-center justify-center font-bold`}>
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-950">{step.title}</h3>
                    {step.highlight && (
                      <span className={`inline-flex items-center gap-1 ${T.bgAcc} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide`}>
                        <Sparkles className="w-3 h-3" /> Clave
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mt-1 leading-relaxed">{step.body}</p>
                  {i === 0 && (
                    <p className="text-xs text-gray-500 mt-2 italic">
                      💡 Doble canal: los clientes pueden hablar por WhatsApp o desde el widget web de tu sitio.
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Optional: practice areas pills (Legal) */}
      {config.practiceAreas && (
        <section className="py-14 px-6" data-testid={`${config.slug}-practice-areas`}>
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-950 mb-3">Áreas de práctica</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
              Configurables desde el dashboard. LegalBot dirige cada consulta al área correcta.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {config.practiceAreas.map((area, i) => (
                <span
                  key={i}
                  className={`${T.pillBg} ${T.pillText} px-4 py-2 rounded-full text-sm font-medium border ${T.borderAcc}`}
                  data-testid={`${config.slug}-area-${i}`}
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Dashboard mockup */}
      <section className="py-16 px-6" data-testid={`${config.slug}-mockup`}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 text-center mb-3">
            Todo bajo control desde un dashboard
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-10">
            Vista previa de cómo se ve la gestión desde ACBotHub.
          </p>
          <div className="rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
            {/* Fake window chrome */}
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-200 bg-gray-50">
              <span className="w-3 h-3 rounded-full bg-red-400" />
              <span className="w-3 h-3 rounded-full bg-amber-400" />
              <span className="w-3 h-3 rounded-full bg-emerald-400" />
              <span className="ml-3 text-xs text-gray-500 font-mono">acbothub.com/dashboard</span>
            </div>

            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              {/* Left panel */}
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-800">{config.mockup.left.title}</h3>
                  <span className="text-xs text-gray-500">{config.mockup.left.subtitle}</span>
                </div>
                <div className="space-y-3">
                  {config.mockup.left.rows.map((row, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{row.primary}</div>
                        <div className="text-xs text-gray-500 truncate">{row.secondary}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {row.link && (
                          <a href="#" onClick={(e) => e.preventDefault()} className="text-xs text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> {row.link}
                          </a>
                        )}
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide ${statusStyles(row.status)}`}>
                          {row.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right panel */}
              <div className={`p-5 sm:p-6 ${T.bgSoft}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-800">{config.mockup.right.title}</h3>
                  {config.mockup.right.action && (
                    <button className={`text-xs font-medium ${T.textAcc} hover:underline`}>
                      {config.mockup.right.action}
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {config.mockup.right.rows.map((row, i) => (
                    <div key={i} className="bg-white rounded-lg px-3 py-3 border border-gray-100 shadow-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-gray-800">{row.primary}</div>
                        {row.badge && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${T.pillBg} ${T.pillText}`}>
                            {row.badge}
                          </span>
                        )}
                      </div>
                      {row.secondary && <div className="text-xs text-gray-500 mt-0.5">{row.secondary}</div>}
                      {row.link && (
                        <a href="#" onClick={(e) => e.preventDefault()} className={`text-xs ${T.textAcc} hover:underline inline-flex items-center gap-1 mt-1`}>
                          <ExternalLink className="w-3 h-3" /> {row.link}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases (rendered only when the config provides them — kits like
          GarzónBot omit this section entirely) */}
      {config.useCases && config.useCases.length > 0 && (
        <section className="py-16 px-6 bg-gray-50" data-testid={`${config.slug}-use-cases`}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 text-center mb-10">
              Perfecto para
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {config.useCases.map((uc, i) => (
                <div
                  key={i}
                  className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex items-start gap-3"
                  data-testid={`${config.slug}-use-case-${i}`}
                >
                  <div className={`w-9 h-9 rounded-lg ${T.bgSoft} flex items-center justify-center shrink-0`}>
                    <Check className={`w-5 h-5 ${T.textAcc}`} />
                  </div>
                  <div className="text-sm font-medium text-gray-800 leading-snug">{uc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Optional: Legal onboarding banner */}
      {config.onboardingBanner && (
        <section className="py-10 px-6" data-testid={`${config.slug}-onboarding-banner`}>
          <div className="max-w-4xl mx-auto bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 sm:p-8 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-950 mb-1">{config.onboardingBanner.title}</h3>
              <p className="text-sm text-amber-900 leading-relaxed">{config.onboardingBanner.body}</p>
            </div>
          </div>
        </section>
      )}

      {/* Extra sections injected by the kit (rendered before the final CTA) */}
      {config.beforeCta}

      {/* Final CTA */}
      <section className="py-16 px-6" data-testid={`${config.slug}-final-cta`}>
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-10 sm:p-14 shadow-xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            ¿Listo para empezar con {config.name}?
          </h2>
          <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
            Actívalo desde tu plan Business y ten tu bot funcionando en minutos.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/register">
              <Button
                data-testid={`${config.slug}-final-cta-btn`}
                size="lg"
                className="bg-white text-indigo-700 hover:bg-indigo-50 shadow-lg rounded-xl font-semibold px-8 py-6 text-base"
              >
                Empezar prueba gratis
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                variant="outline"
                size="lg"
                className="rounded-xl font-semibold px-8 py-6 text-base border-white/40 text-white hover:bg-white/10"
              >
                Ver todos los planes
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-800">← Volver a ACBotHub</Link>
        </div>
      </footer>
    </div>
  );
};

function statusStyles(status) {
  const s = (status || '').toLowerCase();
  if (['pagado', 'confirmado', 'al día', 'al dia', 'disponible'].includes(s)) return 'bg-emerald-100 text-emerald-700';
  if (['en preparación', 'en preparacion', 'en camino', 'novedad', 'en prueba', 'reservado'].includes(s)) return 'bg-amber-100 text-amber-700';
  if (['despachado', 'pendiente'].includes(s)) return 'bg-gray-200 text-gray-700';
  if (['caliente'].includes(s)) return 'bg-red-100 text-red-700';
  if (['tibio'].includes(s)) return 'bg-amber-100 text-amber-700';
  if (['frío', 'frio'].includes(s)) return 'bg-sky-100 text-sky-700';
  return 'bg-gray-100 text-gray-700';
}

export default KitLandingLayout;
